import { useRef, useCallback } from 'react';

interface UseDragScrollOptions {
  /** Ref to the scrollable container */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Callback when manual scroll starts (to pause auto-scroll detection) */
  onManualScrollStart?: () => void;
  /** Callback when manual scroll ends */
  onManualScrollEnd?: () => void;
  /** Delay before considering scroll ended (ms) */
  scrollEndDelay?: number;
}

interface UseDragScrollReturn {
  /** Handler for mouse down event */
  handleMouseDown: (e: React.MouseEvent) => void;
  /** Handler for mouse move event */
  handleMouseMove: (e: React.MouseEvent) => void;
  /** Handler for mouse up/leave event */
  handleMouseUp: () => void;
  /** Whether currently dragging */
  isDragging: React.RefObject<boolean>;
}

/**
 * Hook for drag-to-scroll functionality on horizontal scrollable containers.
 * Commonly used for navigation bars with overflow content.
 */
export function useDragScroll({
  containerRef,
  onManualScrollStart,
  onManualScrollEnd,
  scrollEndDelay = 500,
}: UseDragScrollOptions): UseDragScrollReturn {
  const isDragging = useRef(false);
  const isMouseDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!containerRef.current) return;
    
    isMouseDown.current = true;
    startX.current = e.pageX - containerRef.current.offsetLeft;
    scrollLeft.current = containerRef.current.scrollLeft;
    isDragging.current = false;
  }, [containerRef]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isMouseDown.current || !containerRef.current) return;

    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX.current) * 2;

    // Only start dragging if moved more than 5px (prevents accidental drags)
    if (Math.abs(x - startX.current) > 5) {
      isDragging.current = true;
      containerRef.current.scrollLeft = scrollLeft.current - walk;

      // Notify about manual scroll
      onManualScrollStart?.();
      
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        onManualScrollEnd?.();
      }, scrollEndDelay);
    }
  }, [containerRef, onManualScrollStart, onManualScrollEnd, scrollEndDelay]);

  const handleMouseUp = useCallback(() => {
    isMouseDown.current = false;
    
    // Keep isDragging true briefly to prevent click handler from firing
    setTimeout(() => {
      isDragging.current = false;
    }, 50);
  }, []);

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    isDragging,
  };
}

export default useDragScroll;
